import { useState } from 'react';
import { CheckCircle2, Database, RefreshCw, ShieldCheck, X, XCircle } from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import { useAsync } from '@/lib/use-async';
import { useLocale } from '@/i18n/locale-provider';
import { Badge, Spinner } from '@/components/ui';
import type { Dictionary } from '@/i18n/dictionaries';
import type { ChatMessage, VerificationResult } from '@/lib/types';

/**
 * The verification pane (spec §14.4).
 *
 * Shows the AI's answer against the original data, side by side: the figures it quoted
 * with the arithmetic behind each, the retrievals it ran, and — on demand — the actual
 * records fetched back from the connector so the reader can check them by hand.
 */
export function SourcePanel({ message, onClose }: { message: ChatMessage; onClose: () => void }) {
  const { t, n, isRtl, dateTime, fill } = useLocale();
  const [verification, setVerification] = useState<VerificationResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [openRetrieval, setOpenRetrieval] = useState<string | null>(null);

  const records = useAsync(
    () => (openRetrieval
      ? unwrap<{ records: unknown[]; recordCount: number; checksum: string; queryTemplate: string }>(
          api.connector.get(`/retrieval/${openRetrieval}`),
        )
      : Promise.resolve(null)),
    [openRetrieval],
  );

  async function verify() {
    setVerifying(true);
    try {
      const result = await unwrap<VerificationResult>(api.ai.post(`/chat/messages/${message.id}/verify`));
      setVerification(result);
    } catch {
      setVerification(null);
    } finally {
      setVerifying(false);
    }
  }

  const status = verification?.status ?? message.verificationStatus;

  return (
    <aside
      className="flex w-full max-w-md shrink-0 flex-col overflow-hidden border-s border-mist-200 bg-white
                 xl:max-w-lg"
      aria-label={t.panel.ariaLabel}
    >
      <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-mist-200 px-5">
        <div className="min-w-0">
          <h2 className="font-display text-base font-semibold text-deep-700">{t.panel.title}</h2>
          <p className="truncate text-xs text-mist-500">{t.panel.subtitle}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-2 text-mist-500 hover:bg-mist-100"
          aria-label={t.panel.close}
        >
          <X size={18} />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {/* -------------------------------------------------- verification */}
        <section className="rounded-lg border border-mist-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-deep-700">
              <ShieldCheck size={15} className="text-glow-600" aria-hidden />
              {t.panel.verification}
            </h3>
            <Badge tone={status === 'MATCH' ? 'good' : status === 'PARTIAL_MATCH' ? 'warn' : status === 'UNVERIFIED' ? 'neutral' : 'bad'}>
              {t.message.verification[status] ?? status}
            </Badge>
          </div>

          <p className="mt-2 text-xs leading-relaxed text-mist-600">
            {t.panel.verificationNote}
          </p>

          <button
            type="button"
            onClick={verify}
            disabled={verifying}
            className="btn-secondary mt-3 !py-1.5 !text-xs"
          >
            <RefreshCw size={12} className={verifying ? 'animate-spin' : ''} aria-hidden />
            {verifying ? t.panel.checking : t.panel.recheck}
          </button>

          {verification && (
            <ul className="mt-3 space-y-1.5">
              {verification.checks.map((check) => (
                <li key={check.retrievalId} className="flex items-start gap-2 rounded bg-mist-50 px-2.5 py-2 text-xs">
                  {check.status === 'MATCH' ? (
                    <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-success-fg" aria-hidden />
                  ) : (
                    <XCircle size={13} className="mt-0.5 shrink-0 text-danger-fg" aria-hidden />
                  )}
                  <span className="min-w-0">
                    <code className="text-deep-600">{check.queryTemplate}</code>
                    <span className="mt-0.5 block text-mist-600">{check.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* --------------------------------------------------- calculations */}
        {message.calculations && message.calculations.length > 0 && (
          <section className="mt-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-mist-500">
              {t.panel.calculations}
            </h3>
            <ul className="space-y-2">
              {message.calculations.map((calculation, index) => (
                <li key={index} className="rounded-lg border border-mist-200 p-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-mist-700">
                      {figureLabel(calculation.label, t, isRtl)}
                    </span>
                    <span className="tabular shrink-0 font-display text-base font-semibold text-deep-600">
                      {typeof calculation.value === 'number' ? n(calculation.value) : calculation.value}
                      {calculation.unit && (
                        <span className="ms-1 text-xs font-normal text-mist-500">
                          {t.units[calculation.unit] ?? calculation.unit}
                        </span>
                      )}
                    </span>
                  </div>
                  <p className="ltr-embed mt-1.5 font-mono text-[11px] leading-relaxed text-mist-500">
                    {calculation.method}
                  </p>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-[11px] text-mist-500">
              {t.panel.calculationsNote}
            </p>
          </section>
        )}

        {/* -------------------------------------------------------- filters */}
        {message.filters && (message.filters.from || message.filters.to) && (
          <section className="mt-4 rounded-lg bg-mist-50 p-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-mist-500">{t.panel.dateRange}</h3>
            <p className="tabular mt-1 text-sm text-mist-700">
              {message.filters.from?.slice(0, 10)} → {message.filters.to?.slice(0, 10)}
            </p>
            {message.retrievalPlan && (
              <p className="mt-1.5 text-xs text-mist-500">
                {t.panel.interpretedAs}{' '}
                <strong className="ltr-embed text-mist-700">
                  {message.retrievalPlan.intent.replace(/_/g, ' ').toLowerCase()}
                </strong>
              </p>
            )}
          </section>
        )}

        {/* --------------------------------------------------------- sources */}
        <section className="mt-4">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-mist-500">
            {fill(t.panel.retrievals, { count: n(message.sources?.length ?? 0) })}
          </h3>
          <ul className="space-y-2">
            {message.sources?.map((citation) => (
              <li key={citation.retrievalId} className="rounded-lg border border-mist-200">
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <code className="text-xs font-semibold text-deep-600">{citation.queryTemplate}</code>
                    <Badge tone="info">{citation.entity}</Badge>
                  </div>

                  <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                    <Detail label={t.panel.source} value={citation.sourceId} mono />
                    <Detail label={t.panel.records} value={n(citation.recordCount)} />
                    <Detail label={t.panel.retrieved} value={dateTime(citation.retrievedAt)} />
                    <Detail label={t.panel.checksum} value={`${citation.checksum.slice(0, 12)}…`} mono />
                  </dl>

                  {Object.keys(citation.filters ?? {}).length > 0 && (
                    <p className="ltr-embed mt-2 rounded bg-mist-50 px-2 py-1 font-mono text-[10px] text-mist-600">
                      {Object.entries(citation.filters)
                        .map(([key, value]) => `${key}=${String(value).slice(0, 24)}`)
                        .join('  ')}
                    </p>
                  )}

                  {citation.truncated && (
                    <p className="mt-2 text-[11px] text-warning-fg">
                      {t.panel.truncated}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => setOpenRetrieval(openRetrieval === citation.retrievalId ? null : citation.retrievalId)}
                    className="btn-ghost mt-2 !px-2 !py-1 !text-xs"
                  >
                    <Database size={11} aria-hidden />
                    {openRetrieval === citation.retrievalId ? t.panel.hideRecords : t.panel.showRecords}
                  </button>
                </div>

                {openRetrieval === citation.retrievalId && (
                  <div className="border-t border-mist-200 bg-mist-50 p-3">
                    {records.loading ? (
                      <Spinner label={t.panel.fetchingRecords} />
                    ) : records.error ? (
                      <p className="text-xs text-danger-fg">
                        {t.panel.recordsUnavailable}
                      </p>
                    ) : (
                      <>
                        <p className="mb-2 text-[11px] text-mist-500">
                          {fill(t.panel.recordsExactly, { count: n(records.data?.recordCount ?? 0) })}
                        </p>
                        <pre dir="ltr" className="max-h-64 overflow-auto rounded bg-deep-900 p-3 text-[10px] leading-relaxed text-mist-200">
                          <code>{JSON.stringify(records.data?.records ?? [], null, 2)}</code>
                        </pre>
                      </>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>

        {message.suggestions && message.suggestions.length > 0 && (
          <section className="mt-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-mist-500">
              {t.panel.followUps}
            </h3>
            <ul className="space-y-1.5">
              {message.suggestions.map((suggestion, index) => (
                <li key={index} className="rounded-md bg-glow-50 px-3 py-2 text-xs text-deep-700">
                  {suggestion}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </aside>
  );
}

/**
 * Display name for a computed figure.
 *
 * The AI API stores every label in English so verification and the API contract read the
 * same for everyone; the dictionary maps the fixed ones. Two are composed at runtime from
 * an entity or column name, so they are matched by shape instead.
 */
function figureLabel(label: string, t: Dictionary, isRtl: boolean): string {
  const mapped = t.figures[label];
  if (mapped) return mapped;
  if (!isRtl) return label;

  const matched = label.match(/^(\w+) records matched$/);
  if (matched) return `سجلات ${t.entities[matched[1]] ?? matched[1]} المطابِقة`;

  const summed = label.match(/^Sum of (\w+) across matched records$/);
  if (summed) return `مجموع ${summed[1]} عبر السجلات المطابِقة`;

  return label;
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-mist-500">{label}</dt>
      <dd className={`truncate text-mist-700 ${mono ? 'font-mono' : ''}`} title={value}>{value}</dd>
    </>
  );
}
